import s from 'underscore.string';
import moment from 'moment';

interface FilterNode {
  filters?: FilterNode[];
  logic?: string;
  field?: string;
  operator?: string;
  value?: any;
  [key: string]: any;
}

class Conversor{
  context: any = {};
  operators: Record<string, {term: string; aliases?: string[]}> = {
    eq: {
      term: "eq",
      aliases: ["equals"]
    },
    ne: {
      term: "ne",
      aliases: ["neq"]
    },
    gt: {
      term: "gt",
      aliases: []
    },
    lt: {
      term: "lt",
      aliases: []
    },
    le: {
      term: "le",
      aliases: ["leq"]
    },
    ge: {
      term: "ge",
      aliases: ["geq"]
    },
    and: {
      term: "and",
      aliases: []
    },
    or: {
      term: "or",
      aliases: []
    },
    not: {
      term: "not",
      aliases: []
    },
    anyeq: {
        term: 'anyeq'
    },
    alleq: {
        term: 'alleq'
    }
  };
  functions: Record<string, {function: string; invert?: boolean; aliases?: string[]; format?: (field: any, value: any, rawValue?: any) => string}> = {
    contains: {function: "contains"},
    notcontains: {function: "contains", invert: true},
    endswith: {function: "endswith"},
    startswith: {function: "startswith"},
    anyeq: {function: 'any', format: (field, value) => `${field}/any(t: t eq ${value})`},
    alleq: {function: 'all', format: (field, value) => `${field}/all(t: t eq ${value})`},
    dateeq: {function: 'dateeq', format: (field, value) => {
        const decoded = typeof value === 'string' ? decodeURIComponent(value) : value;
        if(this.isValidDate(decoded)){
            var start = window.encodeURIComponent(moment(decoded).toISOString());
            var end = window.encodeURIComponent(moment(decoded).add(1, 'days').toISOString());
            return `${field} gt ${start} and ${field} lt ${end}`;
        }
        return '';
    }},
    lde: {function: 'lde', format: (field, value) => {
        const decoded = typeof value === 'string' ? decodeURIComponent(value) : value;
        if(this.isValidDate(decoded)){
            var start = window.encodeURIComponent(moment(decoded).add(moment(decoded).utcOffset(), 'minutes').toISOString());
            var end = window.encodeURIComponent(moment(decoded).add(moment(decoded).utcOffset(), 'minutes').add(1, 'days').toISOString());
            return `${field} ge ${start} and ${field} lt ${end}`;
        }
        return '';
    }},
    datebt: {function: 'datebt', format: (field, value) => `${field} ge ${value[0]} and ${field} le ${value[1]}`},
    ingroup: {function: 'ingroup', format: (field, value, rawValues) => {
      let values: any[] = [];
      if(!Array.isArray(rawValues)){
        rawValues = [rawValues];
      }
      let opts = this.context.partnerGroups || [];
      rawValues.forEach((rawValue: any) => {
        let companies = ((opts.filter((r: any) => r.label === rawValue)[0] || {}).value || []).map((r: any) => r.name);
        values = [...values, ...companies];
      });
      values = values.filter((r, i) => values.indexOf(r) === i);
      if(values.length === 0){
        values = ["no partners"];
      }
      return this.getFilterValue(values, field).map((r: any) => field + " eq " + r).join(" or ");
    }}
  };
  defaultOperator = "and";

  constructor(context?: any){
    if(context){
      this.context = context;
    }
  }

  ToOData = function(this: Conversor, filter?: FilterNode | null): string {
    if (!filter || typeof filter !== 'object') {
      return '';
    }
    
    if (filter.filters) {
      var queries = [];
      var operator = ' ' + this.getOperator(filter.logic || this.defaultOperator)
        .term + ' ';
      for (const item of filter.filters) {
        if (!item) continue;
        
        const subQuery = this.ToOData(item);
        if (subQuery && subQuery !== '') {
          queries.push("(" + subQuery + ")");
        }
      }
      
      if (queries.length === 0) {
        return '';
      }
      
      return s.toSentence(queries, operator, operator);
    } else {
      if (!filter.field || !filter.operator) {
        return '';
      }
      
      var functionConfig = this.getFunction(filter.operator);
      var field = null;
      var value = null;
      if (functionConfig) {
        field = filter.field;
        value = this.getFilterValue(filter.value, filter.field);
        
        if(functionConfig.format){
            const formatted = functionConfig.format(field, value, filter.value);
            if (!formatted || formatted === '') {
              return '';
            }
            return formatted;
        }
        if(functionConfig.invert){
            return functionConfig.function + "(" + field + ", " + value + ") eq false";
        }
        return functionConfig.function + "(" + field + ", " + value + ")";
      } else {
        field = filter.field;
        const operatorObj = this.getOperator(filter.operator);
        if (!operatorObj) {
          return '';
        }
        operator = operatorObj.term;
        value = this.getFilterValue(filter.value, filter.field);
        
        // Special handling for null values with ne (not equal) operator
        if (filter.value === null && operator === "ne") {
          return field + " ne null";
        }
        
        // Special handling for null values with eq (equal) operator  
        if (filter.value === null && operator === "eq") {
          return field + " eq null";
        }
        
        if (value === "'undefined'" || value === "undefined") {
          return '';
        }
        
        if(Array.isArray(value)){
          const validValues = value.filter(v => v !== undefined && v !== null && v !== "'undefined'" && v !== "undefined");
          if (validValues.length === 0) {
            return '';
          }
          let joinOp = operator === "ne" ? " and " : " or ";
          return validValues.map(r => field + " " + operator + " " + r).join(joinOp);
        }
        return field + " " + operator + " " + value;
      }
    }
  };
  isValidDate(value: any): boolean {
      if(!value){
          return false;
      }
      if(moment(value, 'YYYY-MM-DD', true).isValid()){
          return true;
      }
      if(moment(value, 'YYYY-MM-DDTHH:mm:ss.SSSZ', true).isValid()){
          return true;
      }
  }
  getFilterValue = function(this: Conversor, value: any, field: string): any {
    if(Array.isArray(value)){
      return value.map(r => this.getFilterValue(r, field));
    }
    if(field.toLowerCase() === 'documenttype'){
        if(value === 'Acknowledgement'){
            value = 'POAck';
        } else if(value === 'Shipment'){
            value = 'AdvanceShipmentNotice';
        }
    }
    if (value instanceof Date){
       return window.encodeURIComponent(value.toISOString());
    }
    else if (value && this.isValidDate(value)){
        return value;
    }
    else if (typeof value === 'string' || value instanceof String) {
        if(value){
            value = window.encodeURIComponent(value.split("'").join("''"));
        }
      return "'" + value + "'";
    }
    else{
      return value;
    }
  };
  getOperator = function(this: Conversor, name: string) {
    if (this.operators[name]) {
      return this.operators[name];
    }
    if (!this.operators[name]) {
      for (var operatorName in this.operators) {
        if (this.operators.hasOwnProperty(operatorName)) {
          var operator = this.operators[operatorName];
          if (operator.aliases && operator.aliases.indexOf(name) > -1) {
            return operator;
          }
        }
      }
    }
    return null;
  };
  getFunction = function(this: Conversor, name: string) {
    if (this.functions[name]) {
      return this.functions[name];
    }
    if (!this.functions[name]) {
      for (var functionName in this.functions) {
        if (this.functions.hasOwnProperty(functionName)) {
          var functionConfig = this.functions[functionName];
          if (functionConfig.aliases && functionConfig.aliases.indexOf(name) >
            -1) {
            return functionConfig;
          }
        }
      }
    }
    return null;
  };
}

export default Conversor;
